import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import {
    Box,
    List,
    ListItemText,
    Paper,
    Typography,
    TextField,
    Button,
    Divider,
    Chip,
    CircularProgress,
    Alert,
    ListItemButton,
    IconButton,
} from "@mui/material";
import Navbar from "../../../../components/shared-menu/Navbar";
import { useWebSocket } from "../../../../services/websocket/WebSocketProvider";
import { API_BASE_URL, API_BASE_URL_NEW, optimo_whatsapp_number } from "../../../../config";
import { AccessTimeFilled, ContentCopy, MoreVert, Send } from "@mui/icons-material";
import { LoanDetailDrawer } from "../dashboard/components/LoanDetailDrawer";
import { useAuth } from "../../../../services/auth/AuthContext";
import fetchApplicationsService from "../../../../services/api/fetchApplications";
import apiRequest from "../../../../services/api/handler";
import { useSnackbar } from "notistack";
import ButtonWithConfirmationDialog from "../../../../components/ButtonWithConfirmationDialog";
import { resellerStatusPerLoanType } from "../dashboard/utils";
import SupportChatService from "../../../../services/api/supportchat";
import InfiniteScroll from 'react-infinite-scroll-component';


const Message = ({ message }) => {
    // Format time in 12-hour format
    let formattedTime = '';
    if (message.created_at) {
        const date = new Date(message.created_at);
        formattedTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    }

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: message.sender_type === "support" ? "flex-end" : "flex-start",
                mb: 0.5,
            }}
        >
            <Box
                sx={{
                    maxWidth: "70%",
                    minWidth: "50px",
                    px: 1.75,
                    pt: 0.5,
                    pb: 0.25,
                    borderRadius: '0.75rem',
                    bgcolor: message.sender_type === "support" ? "primary.main" : "grey.100",
                    color: message.sender_type === "support" ? "white" : "text.primary",
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
                    position: "relative",
                }}
            >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {message.message}
                </Typography>
                {message.pending ? (
                    <Box textAlign={'right'}>
                        <AccessTimeFilled fontSize="2px" />
                    </Box>
                ) : (
                    <Typography
                        variant="caption"
                        display={'block'}
                        fontSize={'0.6875rem'}
                        textAlign={'right'}
                        sx={{ opacity: 0.7 }}
                    >
                        {formattedTime}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

// Group messages by date
const groupMessagesByDate = (messages) => {
    const groups = {};

    messages.forEach(message => {
        if (!message.created_at) return;

        const date = new Date(message.created_at);
        const dateString = date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });

        if (!groups[dateString]) {
            groups[dateString] = [];
        }

        groups[dateString].push(message);
    });

    return groups;
};

// Date separator component
const DateSeparator = ({ date }) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'center',
            my: 2,
        }}
    >
        <Chip
            label={date}
            color="primary"
            variant="outlined"
            size="small"
            sx={{
                backgroundColor: 'background.paper',
                px: 1,
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
            }}
        />
    </Box>
);

function LoanDetailDrawerWithButton({ application, rejectApplication }) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [priority, setPriority] = useState(application.enquiry_details.priority);

    return (
        <>
            <IconButton onClick={() => setDrawerOpen(true)}>
                <MoreVert />
            </IconButton>

            <LoanDetailDrawer
                open={drawerOpen}
                handleClose={() => setDrawerOpen(false)}
                initialData={application}
                propTypes={priority}
                priority={priority}
                setPriority={setPriority}
                rejectApplication={rejectApplication}
            />
        </>
    );
}

const chatStatuses = {
    all: 'All',
    active: 'Active',
    requested: 'Requested',
    ended: 'Ended',
    inactive: 'Inactive',
};

// TODO: use apiRequest
// TODO: modularize
const LiveChat = () => {
    const { setIsAuthenticated } = useAuth();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [chats, setChats] = useState([]);
    const [chatMessages, setChatMessages] = useState([]);
    const [isLoadingChatMessages, setIsLoadingChatMessages] = useState(false);
    const [canLoadMoreMessages, setCanLoadMoreMessages] = useState(true);
    const [newMessage, setNewMessage] = useState("");
    
    // Chat pagination state
    const [hasMoreChats, setHasMoreChats] = useState(true);
    const [isLoadingChats, setIsLoadingChats] = useState(false);

    const [selectedChat, setSelectedChat] = useState(null);
    const [application, setApplication] = useState(null);

    const [searchParams, setSearchParams] = useSearchParams();
    const selectedChatId = searchParams.get("chatId") ?? null;
    const setSelectedChatId = (chat) => {
        setSearchParams((searchParams) => {
            searchParams.set("chatId", chat.id);
            return searchParams;
        });
        setSelectedChat(chat);
        setChatMessages([]);
        setCanLoadMoreMessages(true);
        setApplication(null);
    };

    const chatStatus = searchParams.get("status") ?? 'active';
    const setChatStatus = (status) => {
        setSearchParams((searchParams) => {
            searchParams.set("status", status);
            return searchParams;
        });
    };

    const sessionEnded = selectedChat?.status === "ended";
    const sessionInactive = selectedChat?.status === "inactive";

    const messagesContainerRef = useRef(null);
    const messagesContainerScrollPosition = useRef({
        scrollHeight: 0,
        scrollTop: 0,
    });
    const messagesEndRef = useRef(null);

    const markUnreadChatCountAsZero = useCallback((chatId) => {
        const newChats = [...chats];
        const updatedChatIndex = newChats.findIndex((chat) => String(chat.id) === String(chatId));
        if (updatedChatIndex !== -1) {
            newChats[updatedChatIndex] = {
                ...newChats[updatedChatIndex],
                unread_message_count: 0,
            };
            setChats(newChats);
        }
    }, [chats]);

    const updateActiveChats = useCallback((chatId, latestMessage) => {
        const newChats = [...chats];
        const updatedChatIndex = newChats.findIndex((chat) => chat.id === chatId);
        if (updatedChatIndex !== -1) {
            const updatedChat = {
                ...newChats[updatedChatIndex],
                latest_message: latestMessage,
                unread_message_count: String(chatId) !== selectedChatId
                    ? newChats[updatedChatIndex].unread_message_count + 1
                    : 0,
            };
            newChats.splice(updatedChatIndex, 1);
            newChats.unshift(updatedChat);
        }
        setChats(newChats);
    }, [chats, selectedChatId]);

    const markChatAsRead = useCallback(() => {
        if (!selectedChatId) return;
        SupportChatService.markChatAsRead(
            selectedChatId,
            setIsAuthenticated,
            navigate
        );
    }, [navigate, selectedChatId, setIsAuthenticated]);

    useWebSocket({
        onMessage: useCallback((messageData) => {
            if (String(messageData.active_chat_id) === selectedChatId) {
                setChatMessages((prev) => [
                    ...prev,
                    {
                        ...messageData,
                        chat: messageData.active_chat_id,
                        is_read: true,
                    },
                ]);
                markChatAsRead();
            }

            if (messageData.notification_type === 'new_chat') {
                setChats((prev) => [
                    messageData.additional_data,
                    ...prev,
                ]);
            } else {
                updateActiveChats(messageData.active_chat_id, {
                    message: messageData.message,
                    created_at: messageData.created_at,
                    sender_type: messageData.sender_type,
                });
            }
        }, [markChatAsRead, selectedChatId, updateActiveChats]),
    });

    // Combined function for both initial load and pagination
    const loadChats = useCallback((isInitialLoad = false) => {
        console.log('loadChats called', { isInitialLoad, isLoadingChats, hasMoreChats });
        
        if (isLoadingChats || (!isInitialLoad && !hasMoreChats)) {
            console.log('Early return - already loading or no more chats');
            return;
        }

        setIsLoadingChats(true);

        // Reset state for initial load
        if (isInitialLoad) {
            setChats([]);
            setHasMoreChats(true);
        }

        // Get the last chat ID for pagination (only for non-initial loads)
        const getLastChatId = () => {
            if (isInitialLoad) return null;
            return chats.length > 0 ? chats[chats.length - 1].id : null;
        };

        const lastChatId = getLastChatId();

        SupportChatService.fetchActiveChats(
            { status: chatStatus, lastId: lastChatId },
            setIsAuthenticated,
            navigate
        )
            .then((res) => {
                console.log('Received chats:', res.data?.length, 'has_more:', res.has_more);
                
                if (isInitialLoad) {
                    setChats(res.data || []);
                    // Handle selected chat for initial load
                    if (!selectedChat && res.data && res.data.length > 0) {
                        setSelectedChat(res.data.find((chat) => String(chat.id) === selectedChatId));
                    }
                } else {
                    // Append for pagination
                    setChats(prev => [...prev, ...(res.data || [])]);
                }
                
                setHasMoreChats(res.has_more || false);
            })
            .catch(console.log)
            .finally(() => setIsLoadingChats(false));
    }, [chatStatus, chats, hasMoreChats, isLoadingChats, navigate, selectedChat, selectedChatId, setIsAuthenticated]);

    // Load more chats function for InfiniteScroll
    const loadMoreChats = useCallback(() => {
        loadChats(false);
    }, [loadChats]);

    // Initial load effect
    useEffect(() => {
        console.log('Chat status changed, loading initial chats');
        loadChats(true);
    }, [chatStatus, navigate, setIsAuthenticated]); // Don't include loadChats in dependencies to avoid infinite loop

    // Update loadChats when chatStatus changes but avoid infinite dependency
    const loadChatsRef = useRef(loadChats);
    loadChatsRef.current = loadChats;

    // Simplified initial load effect
    useEffect(() => {
        console.log('Chat status changed, loading initial chats');
        loadChatsRef.current(true);
    }, [chatStatus]);

    // TODO: set has more
    const getMessageHistory = useCallback(async (lastMessageId) => {
        setIsLoadingChatMessages(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/support/get-message-history`, {
                params: {
                    active_chat_id: selectedChatId,
                    last_message_id: lastMessageId,
                },
                headers: {
                    "Authorization": `Bearer ${localStorage.getItem("access_token")}`,
                },
            });
            if (res.data.data.length === 0) {
                setCanLoadMoreMessages(false);
            }
            return res.data.data;
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoadingChatMessages(false);
        }
    }, [selectedChatId]);

    const loadMoreMessages = async () => {
        messagesContainerScrollPosition.current = {
            scrollHeight: messagesContainerRef.current.scrollHeight,
            scrollTop: messagesContainerRef.current.scrollTop,
        };

        const lastMessageId = chatMessages[0]?.id;
        const messages = await getMessageHistory(lastMessageId);
        setChatMessages((prev) => [...messages, ...prev]);
    };

    useEffect(() => {
        if (!selectedChatId || chatMessages.length > 0) return;

        getMessageHistory().then((data) => {
            setChatMessages(data);

            // if selectedChat has unread messages, send a message to the server to mark them as read
            if (data.some((msg) => !msg.is_read)) {
                markChatAsRead();
                markUnreadChatCountAsZero(selectedChatId);
            }
        });
    }, [chatMessages.length, getMessageHistory, markChatAsRead, markUnreadChatCountAsZero, navigate, selectedChat, selectedChatId, setIsAuthenticated]);

    useEffect(() => {
        if (!selectedChat || application) return;
        fetchApplicationsService.getApplicationsForStatusCode(
            null,
            { enquiry_id: selectedChat.enquiry.id },
            setIsAuthenticated,
            navigate
        )
            .then((response) => setApplication(response.applications[0]))
            .catch((err) => console.log('dashboard.search_customers.errors.fetch_failed'))
        // .finally(() => setLoading(false));
    }, [application, navigate, selectedChat, setIsAuthenticated]);

    let lastReadMessageIndex = chatMessages.length - 1;
    for (let i = chatMessages.length - 1; i >= 0; i--) {
        if (chatMessages[i].is_read) {
            lastReadMessageIndex = i;
            break;
        }
    }

    // Group messages by date
    const groupedMessagesByDate = useMemo(() => groupMessagesByDate(chatMessages), [chatMessages]);

    const [isSendingMsg, setIsSendingMsg] = useState(false);
    const sendMessage = () => {
        const message = newMessage.trim();
        if (!message) return;

        setChatMessages((prev) => [
            ...prev,
            {
                id: uuidv4(),
                message: message,
                sender_type: "support",
                created_at: new Date().toISOString(),
                is_read: true,
                pending: true,
            },
        ]);
        setNewMessage("");

        setIsSendingMsg(true);
        SupportChatService.sendSupportMessage(
            {
                enquiryId: selectedChat.enquiry.id,
                message: message,
            },
            setIsAuthenticated,
            navigate,
            selectedChat.enquiry.optimo_phone_id!==optimo_whatsapp_number
        ).then((res) => {
            const messageData = res.message;
            setChatMessages((prev) => [
                ...prev.filter((msg) => !msg.pending),
                messageData,
            ]);
            updateActiveChats(messageData.chat, {
                message: messageData.message,
                created_at: messageData.created_at,
                sender_type: messageData.sender_type,
            });
        })
        .catch((error) => {
            console.error(error);
            enqueueSnackbar("Failed to send message. Please try again", {
                variant: "error",
            });
            setChatMessages(
                (prev) => prev.filter((msg) => !msg.pending)
            );
            setNewMessage(message);
        })
        .finally(() => {
            setIsSendingMsg(false);
        });
    };

    // keep scroll position at same place when more (past) messages are loaded
    const firstMessageId = chatMessages[0]?.id;
    useEffect(() => {
        if (
            messagesContainerScrollPosition.current.scrollHeight !== 0 &&
            messagesContainerRef.current
        ) {
            const newScrollHeight = messagesContainerRef.current.scrollHeight;
            const heightDifference = newScrollHeight - messagesContainerScrollPosition.current.scrollHeight;
            messagesContainerRef.current.scrollTop = messagesContainerScrollPosition.current.scrollTop + heightDifference;
        }
    }, [firstMessageId]);

    // scroll to bottom when a new message is sent/received
    const lastMessageId = chatMessages[chatMessages.length - 1]?.id;
    useEffect(() => {
        if (selectedChat) {
            messagesEndRef.current.scrollIntoView();
        }
    }, [lastMessageId, selectedChat]);

    const copyPhoneNumber = () => {
        navigator.clipboard.writeText(selectedChat.enquiry.phone_number);
        enqueueSnackbar(`Phone number copied to clipboard: ${selectedChat.enquiry.phone_number}`, {
            variant: "success",
        });
    };

    const [isEndingSession, setIsEndingSession] = useState(false);
    const endSession = () => {
        setIsEndingSession(true);
        apiRequest(
            '/support/end-session',
            'POST',
            { chat_id: selectedChatId },
            true,
            setIsAuthenticated,
            navigate,
            false,
            selectedChat.enquiry.optimo_phone_id!==optimo_whatsapp_number
        )
        .then(() => {
            enqueueSnackbar("Session ended successfully", {
                variant: "success",
                autoHideDuration: 5000,
            });
            setChats((prev) => {
                const updatedChats = [...prev];
                const chatIndex = updatedChats.findIndex((chat) => String(chat.id) === selectedChatId);
                if (chatIndex !== -1) {
                    updatedChats[chatIndex] = {
                        ...updatedChats[chatIndex],
                        status: "ended",
                    };
                }
                return updatedChats;
            });
            setSelectedChat((prev) => {
                return {
                    ...prev,
                    status: "ended",
                };
            });
        })
        .catch(() => {
            enqueueSnackbar("Failed to end session. Please try again", {
                variant: "error",
            });
        })
        .finally(() => {
            setIsEndingSession(false);
        });
    };

    const rejectApplication = () => {
        const rejectionCode = Object.keys(
            resellerStatusPerLoanType[application.loan_type.code].rejected_loans
        )[0];

        setApplication((prev) => {
            const newApplication = { ...prev };
            newApplication.current_status_code = rejectionCode;
            newApplication.enquiry_details.application_submission_status = 'rejected';
        });
    };

    const isInitialChatLoad = chats.length === 0 && !isLoadingChats;

    return (
        <Box display="flex" flexDirection="column" height="100vh">
            <Navbar />
            <Box display="flex" height={'100%'} flexDirection="row" flex={1} overflow={'auto'}>
                <Paper sx={{ height: '100%', display: "flex", overflow: 'auto', flexDirection: "column", width: 300, p: 2, mr: 2}}>
                    <Typography variant="h6" gutterBottom>
                        Active Chats
                    </Typography>

                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {Object.entries(chatStatuses).map(([status, label]) => (
                            <Chip
                                key={status}
                                size="small"
                                label={label}
                                color={status === chatStatus ? 'primary' : 'default'}
                                onClick={() => setChatStatus(status)}
                            />
                        ))}
                    </Box>

                    <Box 
                        id="scrollableDiv"
                        sx={{ 
                            // display: 'flex',
                            height: '100%',
                            // maxHeight: 'calc(100vh - 150px)', 
                            overflow: 'auto',
                            flex: 1,
                            mt: 1
                        }}
                    >
                        <InfiniteScroll
                            dataLength={chats.length}
                            scrollThreshold={0.8}
                            next={loadMoreChats}
                            hasMore={hasMoreChats}
                            // height={'100%'}
                            loader={
                                !isInitialChatLoad && (
                                    <Box display="flex" justifyContent="center" p={1}>
                                        <CircularProgress size={20} />
                                    </Box>
                                )
                            }
                            endMessage={
                                chats.length > 0 && (
                                    <Box textAlign="center" p={1}>
                                        <Typography variant="caption" color="textSecondary">
                                            No more chats to load
                                        </Typography>
                                    </Box>
                                )
                            }
                            scrollableTarget="scrollableDiv"
                            // style={{ overflow: 'visible' }}
                        >
                            {chats.length > 0 ? (
                                <List>
                                    {chats.map((chat, i) => (
                                        <Fragment key={chat.id}>
                                            <ListItemButton
                                                selected={selectedChatId === String(chat.id)}
                                                onClick={() => selectedChatId !== String(chat.id) && setSelectedChatId(chat)}
                                                sx={{
                                                    cursor: selectedChatId !== String(chat.id) ? 'pointer' : 'auto',
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <ListItemText
                                                    primary={(
                                                        <Box>
                                                            <Box display="flex" justifyContent="space-between" gap={0.5}>
                                                                <Typography
                                                                    title={chat.enquiry.business_name}
                                                                    fontWeight={chat.unread_message_count > 0 ? '700' : '500'}
                                                                    noWrap
                                                                >
                                                                    {chat.enquiry.business_name || chat.enquiry.phone_number}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    fontSize={'0.6875rem'}
                                                                    textAlign={'right'}
                                                                    whiteSpace={'nowrap'}
                                                                    {...chat.unread_message_count > 0 ? {
                                                                        color: 'primary',
                                                                        fontWeight: '700',
                                                                    } : {}}
                                                                >
                                                                    {new Date().toDateString() === new Date(chat.latest_message?.created_at).toDateString()
                                                                        ? new Date(chat.latest_message?.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
                                                                        : new Date(chat.latest_message?.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).split('/').join('/')}
                                                                </Typography>
                                                            </Box>
                                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                                <Typography
                                                                    variant="body2"
                                                                    title={chat.latest_message?.message}
                                                                    color="text.secondary"
                                                                    fontWeight={chat.unread_message_count > 0 ? '600' : 'normal'}
                                                                    noWrap
                                                                >
                                                                    {chat.latest_message?.message}
                                                                </Typography>

                                                                {chat.status === 'ended' ? (
                                                                    <Chip
                                                                        size="small"
                                                                        color="error"
                                                                        label="Ended"
                                                                    />
                                                                ) : chat.unread_message_count > 0 && (
                                                                    <Chip
                                                                        size="small"
                                                                        color="primary"
                                                                        label={chat.unread_message_count}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                />
                                            </ListItemButton>

                                            {i < chats.length - 1 && (
                                                <Divider sx={{ my: 0.5 }} />
                                            )}
                                        </Fragment>
                                    ))}
                                </List>
                            ) : (
                                !isLoadingChats && (
                                    <Box textAlign="center" p={2}>
                                        <Typography variant="body2" color="textSecondary">
                                            No chats available
                                        </Typography>
                                    </Box>
                                )
                            )}
                        </InfiniteScroll>
                    </Box>
                </Paper>

                <Divider orientation="vertical" flexItem />

                <Box flex={1} p={2} display="flex" flexDirection="column">
                    {selectedChat ? (
                        <>
                            <Box display="flex" justifyContent="space-between" alignItems="center" pb={2}>
                                <Box>
                                    <Typography variant="h6">
                                        Chat with {selectedChat.enquiry.business_name}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={1} fontSize={'1rem'}>
                                        <Typography fontSize={'inherit'}>
                                            {selectedChat.enquiry.first_name} {selectedChat.enquiry.last_name}
                                        </Typography>
                                        <Divider orientation="vertical" flexItem sx={{ borderWidth: 1, my: 0.75 }} />
                                        <Typography
                                            fontSize={'inherit'}
                                            display="flex"
                                            alignItems="center"
                                            gap={0.15}
                                        >
                                            {selectedChat.enquiry.phone_number}
                                            <IconButton
                                                onClick={copyPhoneNumber}
                                                size="small"
                                                color="info"
                                            >
                                                <ContentCopy color="info" />
                                            </IconButton>
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box display="flex" alignItems="center" gap={0.5}>
                                    {!sessionInactive && !sessionEnded && (
                                        <ButtonWithConfirmationDialog
                                            title={'End Session'}
                                            confirmBtnText={'End Session'}
                                            onConfirm={endSession}
                                            isDangerous
                                            button={(handleOpen) => (
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    size="small"
                                                    onClick={handleOpen}
                                                    disabled={isEndingSession}
                                                    startIcon={isEndingSession && <CircularProgress size={16} />}
                                                >
                                                    End Session
                                                </Button>
                                            )}
                                        >
                                            Do you really want to end this session? This cannot be undone.
                                        </ButtonWithConfirmationDialog>
                                    )}

                                    {application && (
                                        <LoanDetailDrawerWithButton
                                            application={application}
                                            rejectApplication={rejectApplication}
                                        />
                                    )}
                                </Box>
                            </Box>
                            <Paper
                                ref={messagesContainerRef}
                                sx={{
                                    flex: 1,
                                    overflowY: "auto",
                                    p: 3,
                                    mb: 2,
                                    backgroundImage: 'linear-gradient(rgba(255,255,255,.5), rgba(255,255,255,.5)), url(/images/doodle-bg.png)',
                                    borderRadius: 2
                                }}
                            >
                                <Box display="flex" justifyContent="center" mb={2}>
                                    {isLoadingChatMessages ? (
                                        <CircularProgress />
                                    ) : canLoadMoreMessages ? (
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={loadMoreMessages}
                                        >
                                            Load More
                                        </Button>
                                    ) : (
                                        <Alert severity="info">
                                            You've reached the start of this conversation
                                        </Alert>
                                    )}
                                </Box>

                                {/* Render read messages grouped by date */}
                                {Object.entries(groupedMessagesByDate).map(([date, messages]) => (
                                    <Fragment key={date}>
                                        <DateSeparator date={date} />

                                        {messages.map((msg) => (
                                            <Fragment key={msg.id}>
                                                <Message key={msg.id} message={msg} />
                                                {lastReadMessageIndex !== chatMessages.length - 1 && chatMessages[lastReadMessageIndex].id === msg.id && (
                                                    <Box
                                                        position="relative"
                                                    >
                                                        <Divider
                                                            sx={{
                                                                my: 3,
                                                                borderWidth: 2,
                                                            }}
                                                        />
                                                        <Chip
                                                            label={`${chatMessages.length - lastReadMessageIndex - 1} New Messages`}
                                                            size="small"
                                                            color="info"
                                                            sx={{
                                                                position: "absolute",
                                                                top: '50%',
                                                                left: '50%',
                                                                transform: 'translate(-50%, -50%)',
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                            </Fragment>
                                        ))}
                                    </Fragment>
                                ))}

                                <Box ref={messagesEndRef} />
                            </Paper>

                            {(sessionEnded || sessionInactive) ? (
                                <Alert severity="info">
                                    This chat session has ended
                                </Alert>
                            ) : (
                                <Box display="flex" gap={1} alignItems="end">
                                    <TextField
                                        fullWidth
                                        placeholder="Type a message"
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        variant="outlined"
                                        multiline
                                        maxRows={2}
                                        onKeyDown={(e) => {
                                            // shift + enter to add new line
                                            // enter to send message
                                            if (e.key === 'Enter') {
                                                if (e.shiftKey) return;
                                                e.preventDefault();
                                                sendMessage();
                                            }
                                        }}
                                        slotProps={{
                                            input: { readOnly: isSendingMsg }
                                        }}
                                        autoFocus
                                    />
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={sendMessage}
                                        endIcon={isSendingMsg ? <CircularProgress size={16} /> : <Send />}
                                        disabled={isSendingMsg}
                                    >
                                        Send
                                    </Button>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Typography variant="h6">Select a chat to start</Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

const LiveChatScreen = (props) => {
    return (
            <LiveChat />
    );
};

export default LiveChatScreen;
